import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { z } from "zod";

import { siteQueryOptions } from "@/api/siteDetails";
import { BroadcastV2ComposeEmailPage } from "@/features/BroadcastV2/components/BroadcastV2ComposeEmailPage";
import { planScheduleWindow } from "@/features/MarketingCalendar/planEvent/draft";
import { planDraftRegistry } from "@/features/MarketingCalendar/planEvent/planDraftRegistry";

const searchSchema = z.object({
  /** Frame to auto-select on mount, set by useGoBack after tile create/edit. */
  pendingFrameId: z.string().optional(),
  /** Set when a Marketing Calendar plan opened this composer; Save and
   * Continue returns to the plan wizard with the new broadcast. */
  returnTo: z.literal("marketing-calendar").optional(),
});

export const Route = createFileRoute(
  "/_auth/dragon-tiles/$siteId/broadcastV2/new/email",
)({
  component: BroadcastV2ComposeEmailRouteComponent,
  validateSearch: zodValidator(searchSchema),
});

export function BroadcastV2ComposeEmailRouteComponent() {
  const { siteId } = Route.useParams();
  const { pendingFrameId, returnTo } = Route.useSearch();
  const { data: site } = useSuspenseQuery(siteQueryOptions(siteId));

  // A plan-wizard entry pre-selects the campaign's audiences on the new
  // broadcast and constrains scheduling to the campaign's dates; the wizard
  // keeps its in-progress draft in this registry.
  const planDraft =
    returnTo === "marketing-calendar"
      ? planDraftRegistry.get(siteId)?.draft
      : undefined;

  return (
    <BroadcastV2ComposeEmailPage
      siteId={siteId}
      defaultFromName={site.default_from_name}
      defaultFromEmail={site.default_from_email}
      defaultReplyTo={site.default_reply_to}
      timezone={site.timezone}
      pendingFrameId={pendingFrameId}
      returnTo={returnTo}
      initialAudiences={planDraft?.audiences ?? []}
      scheduleWindow={planDraft ? planScheduleWindow(planDraft) : null}
    />
  );
}
